import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum, DataRecordTableCell } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { AdminApi, AdminGroup, AdminGroupsListOrderingEnum, AdminGroupsValuesRetrieveFieldEnum } from 'src/api';
import { useDispatch } from 'store/hooks';
import { push } from 'connected-react-router';
import makeLink from 'src/helper/make-link';
import { ISSUER_ROUTES } from '../../pages/routes.config';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import useApiCall from 'src/hooks/use-api-call';
export interface GroupsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  goToDetails: (groupId: string) => void;
  groups?: AdminGroup[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  hasPermission: boolean;
  showOrganizationColumn?: boolean;
  setFilterPopupHeight?: (height: number) => void;
  organizationId?: string;
}

export const GroupsTable: FunctionComponent<GroupsTableProps> = (props) => {
  const {
    className,
    loading,
    hasPermission,
    ordering,
    onOrderBy,
    groups,
    paginationProps,
    goToDetails,
    setFilterPopupHeight,
    showOrganizationColumn = true,
    organizationId,
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const { currentUser } = useCurrentUserSelector();

  const formattedData = groups?.map((group) => ({
    ...group,
    id: String(group.id),
  }));

  const cells: DataRecordTableCell<AdminGroup>[] = [
    {
      title: <Translate name="issuerPermissionsGroupsTable.group" />,
      render: ({ name }) => name,
      orderingFieldName: AdminGroupsListOrderingEnum.NAME_ASC,
      type: CellTypeEnum.text,
      filterName: 'name',
      searchFieldName: AdminGroupsValuesRetrieveFieldEnum.NAME,
      getFilterData: (params: any) =>
        adminApi.adminGroupsValuesRetrieve({ ...params, ...(organizationId && { organizationId: [organizationId] }) }),
    },
    ...(showOrganizationColumn
      ? [
          {
            title: <Translate name="issuerPermissionsGroupsTable.organization" />,
            render: (group: AdminGroup) => ('organizationName' in group ? group.organizationName : ''),
            orderingFieldName: AdminGroupsListOrderingEnum.ORGANIZATION_NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'organizationName',
            searchFieldName: AdminGroupsValuesRetrieveFieldEnum.ORGANIZATION_NAME,
            getFilterData: (params: any) =>
              adminApi.adminGroupsValuesRetrieve({
                ...params,
                ...(organizationId && { organizationId: [organizationId] }),
              }),
          },
        ]
      : []),
  ];

  return (
    <div className={cx('groups-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={formattedData}
        paginationProps={paginationProps}
        loading={loading}
        hasPermission={hasPermission}
        onGoToDetails={goToDetails}
        cells={cells}
        setFilterPopupHeight={setFilterPopupHeight}
      />
    </div>
  );
};

export interface GroupsTableWrapperProps {
  title?: ReactNode;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  skipFilterUrlSync?: boolean;
  hasPermission: boolean;
  organizationId?: string;
  showOrganizationColumn?: boolean;
  setFilterPopupHeight?: (height: number) => void;
  showOrganizationFilter?: boolean;
}

export const GroupsTableWrapper: FunctionComponent<GroupsTableWrapperProps> = WithDataRecord<AdminApi, AdminGroup>(
  AdminApi,
)(
  ({
    data,
    ordering,
    onOrderBy,
    loading,
    hasPermission,
    paginationProps,
    showOrganizationColumn,
    setFilterPopupHeight,
    organizationId,
  }) => {
    const dispatch = useDispatch();

    const goTo = (groupId: string) => {
      dispatch(push(makeLink(ISSUER_ROUTES.groupDetails, { groupId })));
    };

    return (
      <>
        <GroupsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          groups={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          goToDetails={(groupId: string) => goTo(groupId)}
          showOrganizationColumn={showOrganizationColumn}
          setFilterPopupHeight={setFilterPopupHeight}
          organizationId={organizationId}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    const { organizationId, ...restProps } = props as GroupsTableWrapperProps;

    return api.adminGroupsList({
      offset,
      limit,
      ordering: ordering as AdminGroupsListOrderingEnum,
      ...(organizationId && { organizationId: [organizationId] }),
      ...restProps,
    });
  },
);

export default GroupsTableWrapper;
